/**
 * ERSettingsModal — relay-42 Phase 2b (DEC-26).
 *
 * Settings panel (shared .panel classes) with a Projects list. Each project
 * row has a dimmed dot when hidden, the project name, and the shared .tgl
 * switch (38×22) wired to a
 * caller-supplied onToggle handler. Persisting the new hidden_projects
 * array lives in App.tsx alongside the other workspace-doc writes.
 *
 * Locally controlled — open/close lives in App state, NOT the planner-
 * driven `relay_modal` doc. ERModal.tsx is for planner prompts; this
 * is user config.
 */
import { useEscapeToClose } from "../hooks/useEscapeToClose";
import type { ProjectRecord } from "../lib/projectsMigration";
import { isProjectHidden } from "../lib/projectVisibility";

interface Props {
  open: boolean;
  projects: ProjectRecord[];
  hiddenProjects: string[];
  onToggle: (name: string) => void;
  onClose: () => void;
}

export function ERSettingsModal({ open, projects, hiddenProjects, onToggle, onClose }: Props) {
  useEscapeToClose(open, onClose);

  if (!open) return null;

  return (
    <>
      <div className="panel-scrim er-settings-scrim" onClick={onClose} />
      <div
        className="panel er-settings-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="er-settings-title"
      >
        <div className="panel-inner">
          <div className="panel-top">
            <span id="er-settings-title">Settings · Projects</span>
            <button type="button" className="panel-x" onClick={onClose} aria-label="Close" title="Close (Esc)">
              ×
            </button>
          </div>
          <div className="er-settings-body">
            {projects.length === 0 ? (
              <div className="er-settings-empty">No projects yet.</div>
            ) : (
              projects.map(project => {
                const hidden = isProjectHidden(hiddenProjects, project.name);
                return (
                  <div key={project.name} className="er-settings-row">
                    <span
                      aria-hidden
                      className="er-settings-dot"
                      style={{ background: project.color, opacity: hidden ? 0.5 : 1 }}
                    />
                    <span className={`er-settings-name${hidden ? " hidden" : ""}`}>{project.name}</span>
                    <button
                      type="button"
                      role="switch"
                      className="tgl"
                      aria-checked={!hidden}
                      aria-label={`${hidden ? "Show" : "Hide"} ${project.name} in sidebar`}
                      onClick={() => onToggle(project.name)}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
}
